import { Stack, useMantineTheme } from '@mantine/core'
import { BarChart } from '@mantine/charts'
import { z } from 'zod'
import { IconChartHistogram } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useCompetition } from '../../../../common/components/header/api'
import { useCompetitionId } from '../../../../common/hooks'
import {
  Metric,
  useMetricFormatting,
  useMetrics,
  useMetricsData,
} from '../../../../widgets/api'
import { fmt, fmtColor } from '../../../../widgets/format'
import { quarterStart } from '../../../../widgets/quarter'
import { DashboardWidget } from './types'
import {
  ColorSchemaFields,
  ComponentStatisticFields,
  MetricSelect,
  NeutralColorsToggle,
  SubjectFields,
  SubjectSchemaFields,
  TitleInput,
} from './OptionsFormFields'
import { WidgetEmpty, WidgetSection, WidgetSuspense } from './WidgetShell'

// The Month and Quarter bar charts are the same widget with a different period
// axis — `PeriodMode` switches the period series they fetch and plot.
type PeriodMode = 'month' | 'quarter'

const Schema = z.object({
  ...SubjectSchemaFields,
  ...ColorSchemaFields,
  title: z.string().default(''),
  metric_key: z.string().default(''),
  component: z.string().default('percent'),
  statistic: z.string().default('value'),
})

type Settings = z.infer<typeof Schema>

const COMPATIBILITY: Record<string, string[]> = {
  percent: ['value'],
  actual: ['value'],
}

/** Build the period series (period date + axis label) for the given mode. */
function buildPeriods(mode: PeriodMode, year: number) {
  if (mode === 'quarter') {
    return [1, 2, 3, 4].map((q) => ({
      period: quarterStart(year, q),
      label: `Q${q}`,
    }))
  }
  return Array.from({ length: 12 }, (_, i) => {
    const month = (i + 1).toString().padStart(2, '0')
    return {
      period: `${year}-${month}-01`,
      label: dayjs(`${year}-${month}-01`).format('MMM'),
    }
  })
}

function Inner({ mode, settings }: { mode: PeriodMode; settings: Settings }) {
  const { data: allMetrics } = useMetrics()
  const metricKey =
    settings.metric_key ||
    allMetrics.find((m) => m.is_kpi)?.key ||
    allMetrics[0]?.key ||
    ''
  const metric = allMetrics.find((m) => m.key === metricKey)
  if (!metric) return <WidgetEmpty message="No metric available" />
  return <InnerWithMetric mode={mode} metric={metric} settings={settings} />
}

function InnerWithMetric({
  mode,
  metric,
  settings,
}: {
  mode: PeriodMode
  metric: Metric
  settings: Settings
}) {
  const competitionId = useCompetitionId()
  const { data: competition } = useCompetition(competitionId)
  const { data: formatting } = useMetricFormatting()
  const theme = useMantineTheme()
  const metricKey = metric.key

  const competitionYear = competition?.starts_at
    ? dayjs(competition.starts_at).year()
    : dayjs().year()

  const periods = buildPeriods(mode, competitionYear)

  const { data: dataPoints } = useMetricsData({
    period_type: mode,
    period_range: false,
    periods: periods.map((p) => p.period),
    subject_type: settings.subject_type,
    subject_ids: [settings.subject_id],
    metrics: periods.map(() => metricKey),
    components: periods.map(() => settings.component),
    statistics: periods.map(() => settings.statistic),
    competition_id: competitionId,
  })

  const valueByPeriod = new Map<string, number>()
  for (const point of dataPoints) valueByPeriod.set(point.period, point.value)

  const seriesName = metric.short_name || metric.name
  const periodNoun = mode === 'quarter' ? 'Quarterly' : 'Monthly'
  const title = settings.title || `${metric.name} ${periodNoun} Performance`

  // Format axis ticks and bar labels through the shared metric formatter so the
  // chart matches every other widget showing this metric (decimals, percent
  // scaling, prefix/suffix all come from the metric's formatting config).
  const formatValue = (value: number | null | undefined) =>
    fmt(metricKey, settings.component, settings.statistic, value, formatting)

  // Percent values are stored as fractions (1.0 = 100%). With an auto-scaled
  // axis, a series that tops out well below target fills the chart and makes
  // everyone look like a top performer. Pin the axis to at least 100% so bars
  // are read against a true 0–100% scale (and beyond, for over-performers).
  const isPercentAxis = settings.component === 'percent'

  // Raw values drive the bar geometry; fmt() handles the *100 for percent
  // metrics, so no manual value scaling is needed here.
  const chartData = periods.map((p) => {
    const v = valueByPeriod.get(p.period)
    return {
      label: p.label.toUpperCase(),
      [seriesName]: v ?? null,
    }
  })

  return (
    <WidgetSection title={title} boxed padding="24px 24px 24px 64px">
      <BarChart
        h="100%"
        data={chartData}
        dataKey="label"
        series={[{ name: seriesName, color: 'teal.5' }]}
        getBarColor={(value: number) =>
          settings.use_neutral_colors
            ? theme.colors.teal[5]
            : (fmtColor(
                metricKey,
                settings.component,
                settings.statistic,
                value,
                formatting,
                theme
              ) ?? theme.colors.teal[5])
        }
        withLegend={false}
        gridAxis="x"
        withTooltip={false}
        barProps={{ barSize: 30 }}
        yAxisProps={{
          domain: isPercentAxis
            ? ([, dataMax]: [number, number]) => [0, Math.max(dataMax, 1)]
            : [0, 'auto'],
          style: { fontSize: 24, fill: 'white' },
          axisLine: false,
          tickFormatter: formatValue,
        }}
        xAxisProps={{
          style: { fontSize: 24, fill: 'white' },
          interval: 0,
          padding: { left: 20, right: 20 },
          axisLine: false,
          tickLine: false,
        }}
        valueFormatter={formatValue}
      />
    </WidgetSection>
  )
}

/** Build the widget `Component` bound to a period mode. */
function makeComponent(mode: PeriodMode) {
  return function Component({ settings }: { settings: Settings }) {
    return (
      <WidgetSuspense>
        <Inner mode={mode} settings={settings} />
      </WidgetSuspense>
    )
  }
}

function OptionsForm({
  value,
  onChange,
}: {
  value: Settings
  onChange: (next: Settings) => void
}) {
  const patch = (p: Partial<Settings>) => onChange({ ...value, ...p })
  return (
    <Stack gap="xs">
      <TitleInput value={value.title} onChange={(v) => patch({ title: v })} />
      <MetricSelect
        kpiOnly
        value={value.metric_key}
        onChange={(v) => patch({ metric_key: v })}
      />
      <ComponentStatisticFields
        component={value.component}
        statistic={value.statistic}
        compatibility={COMPATIBILITY}
        onChange={(p) => patch(p as Partial<Settings>)}
      />
      <SubjectFields
        subject_type={value.subject_type}
        subject_id={value.subject_id}
        onChange={(p) => patch(p as Partial<Settings>)}
      />
      <NeutralColorsToggle
        value={value.use_neutral_colors}
        onChange={(v) => patch({ use_neutral_colors: v })}
      />
    </Stack>
  )
}

export const KpiBarChartWidget: DashboardWidget<Settings> = {
  type: 'kpi_bar_chart',
  label: 'KPI Bar Chart (Month)',
  description: 'Monthly bar chart for one metric',
  icon: IconChartHistogram,
  minW: 3,
  minH: 2,
  maxW: 6,
  maxH: 3,
  schema: Schema,
  defaultSettings: Schema.parse({}),
  compatibility: COMPATIBILITY,
  Component: makeComponent('month'),
  OptionsForm,
}

export const KpiBarChartQuarterWidget: DashboardWidget<Settings> = {
  type: 'kpi_bar_chart_quarter',
  label: 'KPI Bar Chart (Quarter)',
  description: 'Quarterly bar chart (Q1–Q4) for one metric',
  icon: IconChartHistogram,
  minW: 2,
  minH: 2,
  maxW: 6,
  maxH: 3,
  schema: Schema,
  defaultSettings: Schema.parse({}),
  compatibility: COMPATIBILITY,
  Component: makeComponent('quarter'),
  OptionsForm,
}
